import {
  mergeEvidenceRows,
  normalizeEvidenceSourceType,
  preserveResourceRef,
  resolveResourceIdentity,
} from './evidenceResourceProjection.js';
import { projectSourcingCandidate } from './sourcingCandidateProjection.js';
import { projectSavedSourcingEvidence } from './savedSourcingEvidenceProjection.js';
import { bindEvidenceReadings, projectSavedRetrievalEvidence, projectSourceReadings } from './savedRetrievalEvidenceProjection.js';
import { isPreparedCitation } from '../timeline/citationBindings.js';

const choose = (condition, whenTrue, whenFalse) => (condition ? whenTrue() : whenFalse());

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);

const DEFAULT_EVIDENCE_HIT_SCORE_THRESHOLD = 0.05;

function extractInformativeTerms(value) {
  const normalized = String(value || '').toLowerCase();
  if (!normalized) {
    return [];
  }
  const chinese = normalized.match(/[\u4e00-\u9fa5]{2,}/g) || [];
  const english = normalized.match(/[a-z][a-z0-9_]{2,}/g) || [];
  return Array.from(new Set([...chinese, ...english]))
    .map((term) => term.trim())
    .filter(Boolean);
}

function resolveEvidenceHitScoreThreshold(value) {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) {
    return DEFAULT_EVIDENCE_HIT_SCORE_THRESHOLD;
  }
  if (normalized < 0) {
    return 0;
  }
  if (normalized > 1) {
    return 1;
  }
  return normalized;
}

function isLooseEvidenceHit(normalizedSnippet, normalizedQuery) {
  if (!normalizedQuery || normalizedQuery.length < 2) {
    return true;
  }
  return normalizedSnippet.toLowerCase().includes(normalizedQuery.toLowerCase());
}

function hasStrictEvidenceOverlap(normalizedSnippet, normalizedQuery, matchedTermList) {
  if (matchedTermList.length > 0) {
    return true;
  }
  const queryTerms = extractInformativeTerms(normalizedQuery);
  if (queryTerms.length === 0) {
    return false;
  }
  const snippetTerms = extractInformativeTerms(normalizedSnippet);
  if (snippetTerms.length === 0) {
    return false;
  }
  const overlapCount = queryTerms.filter((term) => snippetTerms.includes(term)).length;
  return overlapCount >= 2;
}

function isEvidenceHit(options, strictMode = true, scoreThreshold = DEFAULT_EVIDENCE_HIT_SCORE_THRESHOLD) {
  const { score, snippet, query, matchedTerms } = { matchedTerms: [], ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  if (Number.isFinite(Number(score))) {
    return Number(score) >= resolveEvidenceHitScoreThreshold(scoreThreshold);
  }
  const normalizedSnippet = String(firstTruthy(() => (snippet), () => (''))).trim();
  const normalizedQuery = String(firstTruthy(() => (query), () => (''))).trim();
  if (firstTruthy(() => (!normalizedSnippet), () => (normalizedSnippet.length < 12))) {
    return false;
  }
  const matchedTermList = choose(Array.isArray(matchedTerms), () => (matchedTerms.map((item) => String(firstTruthy(() => (item), () => (''))).trim()).filter(Boolean)), () => ([]));
  if (!strictMode) {
    return isLooseEvidenceHit(normalizedSnippet, normalizedQuery);
  }
  return hasStrictEvidenceOverlap(normalizedSnippet, normalizedQuery, matchedTermList);
}

function preserveArray(value) {
  return Array.isArray(value) ? value : [];
}

function mapKnowledgeResultToEvidenceRow(result, index, query, runId) {
  const resourceRef = preserveResourceRef(result.resource_ref);
  return {
    key: String(firstTruthy(() => (result.id), () => (result.record_id), () => (result.source_id), () => (resourceRef.resource_id), () => (`${runId}-${index}`))),
    citation_id: firstTruthy(() => (String(result.citation_id || result.citationId || '').trim()), () => (undefined)),
    evidence_kind: 'retrieval',
    title: String(firstTruthy(() => (result.title), () => (result.name), () => (result.vendor_name), () => (result.id), () => (result.record_id), () => (result.source_id), () => (''))).trim(),
    source_type: normalizeEvidenceSourceType(firstTruthy(() => (result.source_type), () => (result.source))),
    score: Number.isFinite(Number(result.score)) ? Number(result.score) : undefined,
    run_id: runId,
    query,
    snippet: String(firstTruthy(() => (result.snippet), () => (result.summary), () => (result.excerpt), () => (result.content), () => (''))).trim(),
    matchedTerms: Array.isArray(result.matched_terms) ? result.matched_terms : [],
    matched_terms: Array.isArray(result.matched_terms) ? result.matched_terms : [],
    resource_ref: resourceRef,
    actions: preserveArray(firstTruthy(() => (resourceRef.actions), () => (result.actions))),
    source_url: firstTruthy(() => (String(result.source_url || result.url || '').trim()), () => (undefined)),
  };
}

function resolveCitationTitle(citation = {}, timelineItems = []) {
  const rawTitle = String(firstTruthy(() => (Object(citation).title), () => (Object(citation).name), () => (''))).trim();
  const sourceId = String(firstTruthy(() => (Object(citation).source_id), () => (''))).trim();
  const opaqueTitle = everyTruthy(() => (rawTitle), () => (sourceId), () => (rawTitle === sourceId));
  if (!opaqueTitle) {
    return rawTitle;
  }
  const attachments = (Array.isArray(timelineItems) ? timelineItems : [])
    .flatMap((item) => (Array.isArray(Object(item).attachments) ? item.attachments : []));
  const matchedAttachment = attachments.find((attachment) => (
    String(firstTruthy(() => (Object(attachment).source_id), () => (''))).trim() === sourceId
  ));
  if (!matchedAttachment) {
    return rawTitle;
  }
  const filename = String(firstTruthy(() => (Object(matchedAttachment).filename), () => (''))).trim();
  const mimeType = String(firstTruthy(() => (Object(matchedAttachment).mime_type), () => (''))).trim().toLowerCase();
  let mimeLabel = '';
  if (firstTruthy(() => (mimeType.includes('word')), () => (mimeType.includes('document')))) {
    mimeLabel = 'document';
  }
  return [firstTruthy(() => (filename), () => (rawTitle)), mimeLabel].filter(Boolean).join(' · ');
}

function mapKnowledgeCitationToEvidenceRow(citation, index, query, runId, timelineItems = []) {
  const resourceRef = preserveResourceRef(citation.resource_ref);
  return {
    key: String(firstTruthy(() => (citation.record_id), () => (citation.source_id), () => (resourceRef.resource_id), () => (citation.citation_id), () => (`${runId}-citation-${index}`))),
    citation_id: firstTruthy(() => (String(citation.citation_id || citation.citationId || '').trim()), () => (undefined)),
    citation_target: citation.citation_target,
    citation_anchor_id: citation.citation_anchor_id,
    reading: citation.reading,
    evidence_kind: 'retrieval',
    title: resolveCitationTitle(citation, timelineItems),
    source_type: normalizeEvidenceSourceType(firstTruthy(() => (citation.source_type), () => (citation.source))),
    score: Number.isFinite(Number(citation.score)) ? Number(citation.score) : undefined,
    run_id: runId,
    query,
    snippet: String(firstTruthy(() => (citation.snippet), () => (citation.matched_text), () => (citation.hit_reason), () => (''))).trim(),
    matchedTerms: Array.isArray(citation.matched_terms) ? citation.matched_terms : [],
    matched_terms: Array.isArray(citation.matched_terms) ? citation.matched_terms : [],
    matched_text: String(firstTruthy(() => (citation.matched_text), () => (''))).trim(),
    source_id: firstTruthy(() => (String(citation.source_id || '').trim()), () => (undefined)),
    resource_ref: resourceRef,
    actions: preserveArray(firstTruthy(() => (resourceRef.actions), () => (citation.actions))),
    source_url: firstTruthy(() => (String(citation.source_url || citation.url || '').trim()), () => (undefined)),
  };
}

export function selectProjectedCanvasEvidenceItems({
  timelineItems,
  artifactDocument,
  evidenceReading,
  readingTargets,
  evidenceStrictMode,
  evidenceHitScoreThreshold = DEFAULT_EVIDENCE_HIT_SCORE_THRESHOLD,
} = {}) {
  // An explicit saved selection is authoritative even when empty or unresolved.
  if (artifactDocument !== undefined) return bindEvidenceReadings([
    ...projectSavedSourcingEvidence(artifactDocument),
    ...projectSavedRetrievalEvidence(artifactDocument, timelineItems),
    ...projectSourceReadings(readingTargets, timelineItems),
  ], evidenceReading);
  const rows = [];
  const seenKeys = new Map();
  const resolvedTimeline = Array.isArray(timelineItems) ? timelineItems : [];

  const pushRow = (row) => {
    const rowKey = row.citation_target ? row.citation_anchor_id : resolveResourceIdentity(row);
    if (!rowKey || seenKeys.has(rowKey)) {
      if (seenKeys.has(rowKey)) {
        const existingIndex = seenKeys.get(rowKey);
        rows[existingIndex] = mergeEvidenceRows(rows[existingIndex], row);
      }
      return;
    }
    seenKeys.set(rowKey, rows.length);
    rows.push(row);
  };

  resolvedTimeline.forEach((item) => {
    const itemType = String(firstTruthy(() => (Object(item).type), () => (''))).trim();
    const payload = choose(everyTruthy(() => (Object(item).payload), () => (typeof item.payload === 'object')), () => (item.payload), () => ({}));

    if (itemType === 'knowledge_search') {
      const query = String(firstTruthy(() => (Object(payload).query), () => (''))).trim();
      const runId = String(firstTruthy(() => (Object(payload).run_id), () => (''))).trim();
      const results = choose(Array.isArray(Object(payload).results), () => (payload.results), () => ([]));
      results.forEach((result, index) => {
        if (firstTruthy(() => (!result), () => (typeof result !== 'object'))) {
          return;
        }
        const nextRow = mapKnowledgeResultToEvidenceRow(result, index, query, runId);
        if (!isEvidenceHit(nextRow, evidenceStrictMode, evidenceHitScoreThreshold)) {
          return;
        }
        pushRow(nextRow);
      });
      return;
    }

    if (itemType === 'knowledge_citation') {
      const query = String(firstTruthy(() => (Object(payload).query), () => (''))).trim();
      const runId = String(firstTruthy(() => (Object(payload).run_id), () => (''))).trim();
      const citations = choose(Array.isArray(Object(payload).citations), () => (payload.citations), () => ([]));
      citations.forEach((citation, index) => {
        if (firstTruthy(() => (!citation), () => (typeof citation !== 'object'))) {
          return;
        }
        const nextRow = mapKnowledgeCitationToEvidenceRow(citation, index, query, runId, resolvedTimeline);
        if (!isPreparedCitation(citation) && !isEvidenceHit({
          ...nextRow,
          snippet: String(firstTruthy(() => (nextRow.snippet), () => (nextRow.matched_text), () => (''))).trim(),
        }, evidenceStrictMode, evidenceHitScoreThreshold)) {
          return;
        }
        pushRow(nextRow);
      });
      return;
    }

    if (itemType === 'sourcing_candidates') {
      const query = String(firstTruthy(() => (Object(payload).query), () => (Object(payload).message_excerpt), () => (''))).trim();
      const runId = String(firstTruthy(() => (Object(payload).run_id), () => (Object(Object(payload).source_meta).retrieval_batch), () => (''))).trim();
      const candidates = choose(Array.isArray(Object(payload).candidates), () => (payload.candidates), () => ([]));
      const eventSourceMeta = choose(everyTruthy(() => (Object(payload).source_meta), () => (typeof payload.source_meta === 'object'), () => (!Array.isArray(payload.source_meta))), () => (payload.source_meta), () => ({}));
      candidates.forEach((candidate, index) => {
        if (firstTruthy(() => (!candidate), () => (typeof candidate !== 'object'))) {
          return;
        }
        const nextRow = projectSourcingCandidate({ candidate, index, query, runId, eventSourceMeta });
        if (nextRow) {
          pushRow(nextRow);
        }
      });
    }
  });

  return bindEvidenceReadings(rows, evidenceReading);
}

export default selectProjectedCanvasEvidenceItems;
